
import React, { useState } from 'react';
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { AssessmentQuestion } from './AssessmentQuestion';

interface ProductOperationsSectionProps {
  onComplete: (data: { score: number; responses: Record<string, number> }) => void;
  initialData?: { score: number; responses: Record<string, number> };
}

export const ProductOperationsSection = ({ onComplete, initialData }: ProductOperationsSectionProps) => {
  const { toast } = useToast();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState<Record<string, number>>(
    initialData?.responses || {}
  );

  const questions = [
    {
      id: 'processExcellence',
      title: 'Process Excellence',
      question: 'How do you optimize product operations?',
      options: [
        { text: 'I follow established processes effectively and help product development stay on track. I ensure quality delivery within my scope.', points: 2 },
        { text: 'I improve processes and remove obstacles that slow down product development. I make workflows more efficient for myself and immediate collaborators.', points: 4 },
        { text: 'I optimize product operations that benefit multiple teams or products. I create systems that make product development more effective at scale.', points: 6 },
        { text: 'I develop new methodologies that others adopt across the organization. My approaches to product development influence how multiple teams work.', points: 8 }
      ],
      evidence: [
        { 
          title: 'Process Improvement',
          description: 'Share an example of a process improvement you implemented'
        },
        {
          title: 'Efficiency Gain',
          description: 'What measurable efficiency was gained?'
        }
      ]
    },
    {
      id: 'teamLeadership',
      title: 'Team Leadership',
      question: 'How do you influence others in product development?',
      options: [
        { text: 'I collaborate effectively with cross-functional partners. I\'m a reliable teammate who contributes to team cohesion.', points: 1 },
        { text: 'I mentor other product professionals and help them grow their skills. I share knowledge and experiences to help others improve.', points: 3 },
        { text: 'I build and lead high-performing product teams that consistently deliver excellent results. I bring out the best in diverse team members.', points: 5 },
        { text: 'I develop the next generation of product leaders throughout the organization. My mentees become successful product leaders who drive significant initiatives.', points: 7 }
      ],
      evidence: [
        {
          title: 'Leadership Example',
          description: 'Describe your most significant team leadership experience'
        },
        {
          title: 'Team Impact',
          description: 'How did your leadership improve team outcomes?'
        }
      ]
    }
  ];

  const handleSelect = (points: number) => {
    const currentQuestionId = questions[currentQuestion].id;
    setResponses(prev => ({
      ...prev,
      [currentQuestionId]: points
    }));
  };

  const calculateScore = (responses: Record<string, number>): number => {
    return Object.values(responses).reduce((sum, points) => sum + points, 0);
  };

  const handleNext = () => {
    if (currentQuestion === questions.length - 1) {
      const finalData = {
        score: calculateScore(responses),
        responses
      };
      
      toast({
        title: "Section completed",
        description: "Your Product Operations & Team Leadership assessment has been saved.",
      });
      
      onComplete(finalData);
    } else {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentQuestion(prev => Math.max(0, prev - 1));
  };

  const currentQuestionData = questions[currentQuestion];
  const selectedPoints = responses[currentQuestionData.id];

  return (
    <Card className="max-w-3xl mx-auto">
      <AssessmentQuestion
        title={currentQuestionData.title}
        question={currentQuestionData.question}
        options={currentQuestionData.options}
        evidence={currentQuestionData.evidence}
        onNext={handleNext}
        onPrevious={handlePrevious}
        onSelect={handleSelect}
        selectedPoints={selectedPoints}
        showPrevious={currentQuestion > 0}
      />
    </Card>
  );
};

export default ProductOperationsSection;
